import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSocket } from "../hooks/useSocket";
import { sessionService } from "../services/session.service";

export function WaitingRoom() {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const socket = useSocket();
  const [players, setPlayers] = useState<
    Array<{ id: string; name: string; cardsCount: number; hasSaidUNO: boolean }>
  >([]);
  const [spectators, setSpectators] = useState<
    Array<{ id: string; name: string }>
  >([]);
  const [hasGame, setHasGame] = useState(false);
  const [isPlayer, setIsPlayer] = useState(false);
  const [isSpectator, setIsSpectator] = useState(false);
  const [error, setError] = useState("");
  const [isHost, setIsHost] = useState(false);
  const [copiedRoomId, setCopiedRoomId] = useState(false);
  
  // Track socketId to detect reconnections and reset join guard
  const socketIdRef = useRef<string | undefined>(socket.getSocketId());
  // Track if we've already attempted to join room to prevent duplicate joins
  const hasJoinedRoomRef = useRef(false);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const state = location.state as { playerName?: string; isHost?: boolean };
    if (state?.isHost) {
      setIsHost(true);
    }
  }, [location]);

  useEffect(() => {
    if (!roomId) {
      navigate("/");
      return;
    }

    // Check if socketId changed (reconnection) - reset join guard if so
    const currentSocketId = socket.getSocketId();
    if (socketIdRef.current !== currentSocketId) {
      console.log("[WaitingRoom] Socket ID changed:", socketIdRef.current, "->", currentSocketId);
      socketIdRef.current = currentSocketId;
      // Reset join guard when socketId changes (new socket lifecycle)
      hasJoinedRoomRef.current = false;
      // Reset join guard in socket client as well
      socket.resetJoinGuard(roomId);
    }

    // Reset join flag when roomId changes
    if (hasJoinedRoomRef.current && socketIdRef.current === currentSocketId) {
      // Only reset if roomId changed, not if socketId changed (handled above)
      // Actually, keep the guard if same socketId and roomId - don't reset
    }

    // Cleanup any pending reconnect timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    const state = location.state as { playerName?: string; isHost?: boolean };
    let playerName = state?.playerName || "";

    // Try to get player name from session if not provided
    if (!playerName) {
      const session = sessionService.getSession();
      if (session?.playerName) {
        playerName = session.playerName;
      }
    }

    // Kiểm tra localStorage để reconnect nếu page bị reload
    const savedRoomInfo = localStorage.getItem("uno_room");
    if (savedRoomInfo) {
      try {
        const roomInfo = JSON.parse(savedRoomInfo);
        if (roomInfo.roomId === roomId && roomInfo.playerName && !playerName) {
          // Page reload - tự động reconnect với session
          // Only reconnect if we haven't already joined with current socketId
          if (!hasJoinedRoomRef.current && currentSocketId) {
            console.log("[WaitingRoom] Page reload detected, reconnecting to room:", roomId);
            setIsHost(roomInfo.isHost || false);
            hasJoinedRoomRef.current = true;
            
            // Đợi socket connect trước khi join room
            const attemptReconnect = () => {
              const socketId = socket.getSocketId();
              // Check if socketId changed during wait
              if (socketId !== currentSocketId) {
                console.log("[WaitingRoom] Socket ID changed during reconnect wait, resetting guard");
                hasJoinedRoomRef.current = false;
                socketIdRef.current = socketId;
                socket.resetJoinGuard(roomId);
                return;
              }
              
              if (socket.isSocketConnected() && socketId) {
                // Use session player name if available, otherwise use saved room info
                const session = sessionService.getSession();
                const reconnectPlayerName = session?.playerName || roomInfo.playerName;
                console.log("[WaitingRoom] Attempting to join room:", roomId, "socket:", socketId);
                socket.joinRoom(roomId, reconnectPlayerName);
                reconnectTimeoutRef.current = null;
              } else {
                // Đợi thêm một chút và thử lại
                reconnectTimeoutRef.current = setTimeout(attemptReconnect, 500);
              }
            };
            attemptReconnect();
          }
        }
      } catch (e) {
        console.error("[WaitingRoom] Error parsing saved room info:", e);
      }
    }

    // Nếu có playerName từ location.state, lưu vào localStorage (backward compatibility)
    // Note: If we have playerName from location.state, it means we navigated from RoomManagement
    // and join was already called. We should NOT join again here to avoid duplicate joins.
    if (playerName && state?.isHost !== undefined && currentSocketId) {
      localStorage.setItem("uno_room", JSON.stringify({
        roomId,
        playerName,
        isHost: state.isHost,
      }));
      // Mark as joined since join was already called from RoomManagement
      // The ROOM_JOINED event will confirm this
      // Only mark if socketId matches (same socket lifecycle)
      if (socketIdRef.current === currentSocketId) {
        hasJoinedRoomRef.current = true;
      }
    }

    const handleRoomUpdated = (data: {
      roomId: string;
      players: Array<{
        id: string;
        name: string;
        cardsCount: number;
        hasSaidUNO: boolean;
      }>;
      spectators: Array<{ id: string; name: string }>;
      hasGame: boolean;
    }) => {
      console.log("[WaitingRoom] ROOM_UPDATED received:", data);
      if (data.roomId === roomId) {
        setPlayers(data.players);
        setSpectators(data.spectators || []);
        setHasGame(data.hasGame || false);
        
        // Check if current user is a player or spectator
        const userId = sessionService.getUserId();
        const isPlayerCheck = data.players.some((p) => p.id === userId);
        const isSpectatorCheck = data.spectators.some((s) => s.id === userId);
        setIsPlayer(isPlayerCheck);
        setIsSpectator(isSpectatorCheck);
        
        // Mark as joined once we receive room update (only if socketId hasn't changed)
        const currentSocketIdCheck = socket.getSocketId();
        if ((isPlayerCheck || isSpectatorCheck) && socketIdRef.current === currentSocketIdCheck) {
          hasJoinedRoomRef.current = true;
        }
        
        // Check if user is host (first player)
        if (data.players.length > 0 && data.players[0].id === userId) {
          setIsHost(true);
        }
      }
    };

    const handleGameStarted = (data: { roomId: string }) => {
      if (data.roomId === roomId) {
        console.log("[WaitingRoom] GAME_STARTED received");
        setHasGame(true);
        // Don't auto-navigate here, wait for GAME_STATE_UPDATED
      }
    };

    const handleGameStateUpdated = (state: any) => {
      if (state.roomId === roomId) {
        console.log("[WaitingRoom] GAME_STATE_UPDATED received, state:", state);
        setHasGame(true);
        
        // Auto-navigate to game if user is a player and has cards
        const userId = sessionService.getUserId();
        if (userId) {
          const hasHand = state.hands && state.hands[userId] && state.hands[userId].length > 0;
          const isPlayerCheck = state.players && state.players.some((p: any) => p.id === userId);
          
          console.log("[WaitingRoom] Checking navigation:", { userId, hasHand, isPlayerCheck });
          
          if (isPlayerCheck && hasHand) {
            console.log("[WaitingRoom] Auto-navigating to game");
            // Small delay to ensure state is updated
            setTimeout(() => {
              navigate(`/game/${roomId}`);
            }, 300);
          }
        }
      }
    };

    const handleError = (data: { message: string }) => {
      setError(data.message);
    };

    const handlePlayerLeft = () => {
      // Refresh room state
      if (roomId) {
        socket.getRoomState(roomId);
      }
    };

    const handleRoomJoined = (data: { roomId: string; userId?: string; playerName?: string; hasGame?: boolean; isPlayer?: boolean; isSpectator?: boolean }) => {
      if (data.roomId === roomId) {
        console.log("[WaitingRoom] ROOM_JOINED received:", data);
        // Mark as joined when we receive ROOM_JOINED event (only if socketId hasn't changed)
        const currentSocketIdCheck = socket.getSocketId();
        if (socketIdRef.current === currentSocketIdCheck) {
          hasJoinedRoomRef.current = true;
        }
      }
    };

    const handleNavigateToGame = (data: { roomId: string }) => {
      if (data.roomId === roomId) {
        console.log("[WaitingRoom] NAVIGATE_TO_GAME received, navigating to game");
        // User is already in game, navigate to game page
        navigate(`/game/${roomId}`);
      }
    };

    socket.onRoomJoined(handleRoomJoined);
    socket.onNavigateToGame(handleNavigateToGame);
    socket.onRoomUpdated(handleRoomUpdated);
    socket.onGameStarted(handleGameStarted);
    socket.onGameStateUpdated(handleGameStateUpdated);
    socket.onError(handleError);
    socket.onPlayerLeft(handlePlayerLeft);

    // Request room state when component mounts to ensure we have the latest data
    // This handles the case where ROOM_UPDATED was emitted before listeners were set up
    const requestTimeout = setTimeout(() => {
      if (roomId) {
        console.log("[WaitingRoom] Requesting room state for:", roomId);
        socket.getRoomState(roomId);
      }
    }, 200);

    return () => {
      clearTimeout(requestTimeout);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      socket.off("ROOM_JOINED", handleRoomJoined);
      socket.off("NAVIGATE_TO_GAME", handleNavigateToGame);
      socket.off("ROOM_UPDATED", handleRoomUpdated);
      socket.off("GAME_STARTED", handleGameStarted);
      socket.off("GAME_STATE_UPDATED", handleGameStateUpdated);
      socket.off("ERROR", handleError);
      socket.off("PLAYER_LEFT", handlePlayerLeft);
    };
  }, [roomId, navigate, socket]); // Removed 'location' from dependencies to prevent re-runs

  const handleStartGame = () => {
    if (!roomId) return;
    if (players.length < 2) {
      setError("Need at least 2 players to start");
      return;
    }
    socket.startGame(roomId);
  };

  const handleJoinAsPlayer = () => {
    if (!roomId) return;
    socket.joinAsPlayer(roomId);
  };

  const handleEnterGame = () => {
    if (!roomId) return;
    // Simply navigate to game page - Game.tsx will handle loading state and request game state
    navigate(`/game/${roomId}`);
  };

  // Check if there are empty player slots
  const hasEmptySlot = players.length < 4;
  const canJoinAsPlayer = isSpectator && hasEmptySlot;

  // Copy room ID to clipboard
  const handleCopyRoomId = async () => {
    if (!roomId) return;
    try {
      await navigator.clipboard.writeText(roomId);
      setCopiedRoomId(true);
      setTimeout(() => setCopiedRoomId(false), 2000);
    } catch (err) {
      console.error("Failed to copy room ID:", err);
    }
  };

  // Create fixed 4 player slots
  const playerSlots = useMemo(() => {
    const slots = Array.from({ length: 4 }, (_, index) => {
      const player = players[index];
      return {
        id: player?.id || `empty-${index}`,
        player: player || null,
        isEmpty: !player,
        isHost: index === 0 && player !== null,
      };
    });
    return slots;
  }, [players]);

  // Add CSS animations for subtle effects
  useEffect(() => {
    const style = document.createElement("style");
    style.textContent = `
      @keyframes fadeIn {
        from { opacity: 0; transform: translateY(-10px); }
        to { opacity: 1; transform: translateY(0); }
      }
      @keyframes slideIn {
        from { opacity: 0; transform: translateX(-20px); }
        to { opacity: 1; transform: translateX(0); }
      }
      @keyframes pulse {
        0%, 100% { opacity: 1; }
        50% { opacity: 0.6; }
      }
      @keyframes shimmer {
        0% { background-position: -1000px 0; }
        100% { background-position: 1000px 0; }
      }
      .player-slot-enter {
        animation: fadeIn 0.3s ease-out;
      }
      .player-slot-occupied {
        animation: slideIn 0.3s ease-out;
      }
      .start-button-enabled {
        animation: pulse 2s ease-in-out infinite;
      }
      .waiting-dots::after {
        content: '...';
        animation: pulse 1.5s ease-in-out infinite;
      }
      .empty-slot-shimmer {
        background: linear-gradient(90deg, rgba(40,40,60,0.6) 0%, rgba(50,50,70,0.8) 50%, rgba(40,40,60,0.6) 100%);
        background-size: 2000px 100%;
        animation: shimmer 2s infinite;
      }
      .room-header-pulse {
        animation: pulse 3s ease-in-out infinite;
      }
      .player-slot:hover:not(.player-slot-empty) {
        transform: translateY(-4px);
        box-shadow: 0 8px 24px rgba(0,0,0,0.4) !important;
        border-color: rgba(255,255,255,0.3) !important;
      }
      .copy-button:hover {
        background: rgba(255,255,255,0.2) !important;
        border-color: rgba(255,255,255,0.3) !important;
        transform: scale(1.05);
      }
      .start-button-enabled:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 6px 16px rgba(102, 126, 234, 0.5) !important;
      }
      .enter-game-button:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 16px rgba(72, 187, 120, 0.5) !important;
      }
      .join-as-player-button:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 16px rgba(66, 153, 225, 0.4) !important;
      }
      @media (max-width: 768px) {
        .player-slots-grid {
          grid-template-columns: 1fr !important;
        }
        .action-area button {
          width: 100% !important;
        }
        .room-id-value {
          flex-direction: column !important;
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        {/* Room Header Zone */}
        <div className="room-header" style={styles.roomHeader}>
          <div style={styles.roomHeaderTop}>
            <h1 style={styles.roomTitle}>Room Lobby</h1>
            {!hasGame && players.length < 2 && (
              <div className="room-header-pulse" style={styles.waitingIndicator}>
                ⏳
              </div>
            )}
          </div>
          <div style={styles.roomIdContainer}>
            <div style={styles.roomIdLabel}>Room ID</div>
            <div style={styles.roomIdValue}>
              <code style={styles.roomIdCode}>{roomId}</code>
              <button
                onClick={handleCopyRoomId}
                className="copy-button"
                style={styles.copyButton}
                title="Copy room ID"
              >
                {copiedRoomId ? "✓ Copied" : "📋"}
              </button>
            </div>
          </div>
          <div style={styles.playerCount}>
            {players.length} / 4 players
            {!hasGame && players.length < 2 && (
              <span style={styles.waitingText}> • Waiting for players...</span>
            )}
            {hasGame && <span style={styles.gameInProgress}> • Game in progress</span>}
          </div>
        </div>

        {error && <div style={styles.error}>{error}</div>}

        {/* Player Slots Zone */}
        <div style={styles.playerSlotsSection}>
          <h2 style={styles.playerSlotsTitle}>Players</h2>
          <div className="player-slots-grid" style={styles.playerSlotsGrid}>
            {playerSlots.map((slot, index) => (
              <div
                key={slot.id}
                className={`${slot.player ? "player-slot-occupied" : "player-slot-enter"} ${slot.isEmpty ? "player-slot-empty" : ""}`}
                style={{
                  ...styles.playerSlot,
                  ...(slot.isEmpty ? styles.playerSlotEmpty : {}),
                }}
              >
                {slot.player ? (
                  <>
                    <div style={styles.playerSlotHeader}>
                      <div style={styles.playerAvatar}>
                        {slot.player.name.charAt(0).toUpperCase()}
                      </div>
                      {slot.isHost && (
                        <div style={styles.hostCrown} title="Host">
                          👑
                        </div>
                      )}
                    </div>
                    <div style={styles.playerSlotName}>{slot.player.name}</div>
                    {hasGame && slot.player.cardsCount > 0 && (
                      <div style={styles.playerSlotCards}>
                        {slot.player.cardsCount} cards
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <div style={styles.emptySlotIcon}>👤</div>
                    <div style={styles.emptySlotText}>
                      {index === 0 && players.length === 0
                        ? "Waiting for players..."
                        : "Waiting for player..."}
                    </div>
                    {!hasGame && (
                      <div style={styles.emptySlotHint}>Invite a friend</div>
                    )}
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Spectators Section (if any) */}
        {spectators.length > 0 && (
          <div style={styles.spectatorsSection}>
            <h3 style={styles.spectatorsTitle}>Spectators ({spectators.length})</h3>
            <div style={styles.spectatorsList}>
              {spectators.map((spectator) => (
                <div key={spectator.id} style={styles.spectatorBadge}>
                  {spectator.name}
                  {spectator.id === sessionService.getUserId() && (
                    <span style={styles.youBadge}>You</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Area Zone */}
        <div className="action-area" style={styles.actionArea}>
          {hasGame && isPlayer && (
            <button
              onClick={handleEnterGame}
              className="enter-game-button"
              style={styles.enterGameButton}
            >
              Enter Game
            </button>
          )}

          {!hasGame && isHost && (
            <button
              onClick={handleStartGame}
              disabled={players.length < 2}
              className={`start-button-enabled ${players.length >= 2 ? "start-button-enabled" : ""}`}
              style={{
                ...styles.startButton,
                ...(players.length < 2 ? styles.startButtonDisabled : {}),
              }}
            >
              {players.length < 2 ? "Need at least 2 players" : "Start Game"}
            </button>
          )}

          {!hasGame && !isHost && isPlayer && (
            <div style={styles.waitingForHost}>
              <div className="waiting-dots" style={styles.waitingForHostText}>
                Waiting for host to start
              </div>
            </div>
          )}

          {canJoinAsPlayer && (
            <button
              onClick={handleJoinAsPlayer}
              className="join-as-player-button"
              style={styles.joinAsPlayerButton}
            >
              Join as Player
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "var(--bg)",
    padding: "20px",
  },
  card: {
    background: "var(--bg-card)",
    // backdropFilter removed for Casual Party (solid backgrounds, no blur)
    borderRadius: "20px",
    padding: "32px",
    boxShadow: "0 20px 60px rgba(0,0,0,0.15)",
    maxWidth: "700px",
    width: "100%",
    border: "1px solid var(--border-subtle)",
  },
  // Room Header Zone
  roomHeader: {
    marginBottom: "32px",
    textAlign: "center",
    paddingBottom: "24px",
    borderBottom: "2px solid var(--border-medium)",
  },
  roomHeaderTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "12px",
    marginBottom: "16px",
  },
  roomTitle: {
    margin: "0",
    fontSize: "32px",
    fontWeight: "700",
    color: "var(--text)",
    letterSpacing: "-0.5px",
  },
  waitingIndicator: {
    fontSize: "24px",
  },
  roomIdContainer: {
    marginBottom: "12px",
  },
  roomIdLabel: {
    fontSize: "12px",
    fontWeight: "600",
    color: "var(--text-muted)",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: "6px",
  },
  roomIdValue: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
  },
  roomIdCode: {
    fontSize: "18px",
    fontWeight: "700",
    fontFamily: "monospace",
    color: "var(--text)",
    background: "var(--bg-card-hover)",
    padding: "8px 16px",
    borderRadius: "10px",
    border: "2px solid var(--border-medium)",
    letterSpacing: "1px",
  },
  copyButton: {
    padding: "8px 12px",
    fontSize: "16px",
    background: "var(--bg-card-hover)",
    border: "2px solid var(--border-medium)",
    borderRadius: "10px",
    cursor: "pointer",
    transition: "all 0.2s",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "var(--text)",
  },
  playerCount: {
    fontSize: "18px",
    fontWeight: "600",
    color: "var(--text)",
    marginTop: "8px",
  },
  waitingText: {
    color: "var(--text-muted)",
    fontWeight: "400",
  },
  gameInProgress: {
    color: "var(--accent)",
    fontWeight: "600",
  },
  // Player Slots Zone
  playerSlotsSection: {
    marginBottom: "32px",
  },
  playerSlotsTitle: {
    fontSize: "22px",
    fontWeight: "600",
    color: "var(--text)",
    marginBottom: "20px",
    textAlign: "center",
  },
  playerSlotsGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "16px",
  },
  playerSlot: {
    background: "var(--bg-card-hover)",
    border: "2px solid var(--border-subtle)",
    borderRadius: "16px",
    padding: "20px",
    minHeight: "140px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    transition: "all 0.3s ease",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  },
  playerSlotEmpty: {
    background: "var(--bg-card)",
    borderStyle: "dashed",
    borderColor: "var(--border-medium)",
  },
  playerSlotHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    marginBottom: "12px",
    position: "relative",
  },
  playerAvatar: {
    width: "56px",
    height: "56px",
    borderRadius: "50%",
    background: "var(--btn-primary)",
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "24px",
    fontWeight: "700",
    boxShadow: "0 4px 12px color-mix(in srgb, var(--accent) 30%, transparent)",
  },
  hostCrown: {
    position: "absolute",
    top: "-8px",
    right: "-12px",
    fontSize: "24px",
    filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.2))",
  },
  playerSlotName: {
    fontSize: "16px",
    fontWeight: "600",
    color: "var(--text)",
    marginBottom: "4px",
    textAlign: "center",
  },
  playerSlotCards: {
    fontSize: "13px",
    color: "var(--text-muted)",
    fontWeight: "500",
  },
  emptySlotIcon: {
    fontSize: "48px",
    opacity: "0.3",
    marginBottom: "8px",
  },
  emptySlotText: {
    fontSize: "14px",
    color: "var(--text-muted)",
    fontWeight: "500",
    textAlign: "center",
    marginBottom: "4px",
  },
  emptySlotHint: {
    fontSize: "12px",
    color: "var(--text-muted)",
    fontStyle: "italic",
    textAlign: "center",
    opacity: 0.7,
  },
  // Spectators Section
  spectatorsSection: {
    marginBottom: "24px",
    paddingTop: "24px",
    borderTop: "1px solid var(--border-subtle)",
  },
  spectatorsTitle: {
    fontSize: "16px",
    fontWeight: "600",
    color: "var(--text-muted)",
    marginBottom: "12px",
    textAlign: "center",
  },
  spectatorsList: {
    display: "flex",
    flexWrap: "wrap",
    gap: "8px",
    justifyContent: "center",
  },
  spectatorBadge: {
    padding: "6px 12px",
    background: "var(--bg-card-hover)",
    border: "1px solid var(--border-medium)",
    borderRadius: "12px",
    fontSize: "13px",
    color: "var(--text)",
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },
  youBadge: {
    padding: "2px 6px",
    background: "var(--accent)",
    color: "white",
    borderRadius: "6px",
    fontSize: "10px",
    fontWeight: "600",
  },
  // Action Area Zone
  actionArea: {
    marginTop: "24px",
    paddingTop: "24px",
    borderTop: "2px solid var(--border-medium)",
  },
  startButton: {
    width: "100%",
    padding: "16px 24px",
    fontSize: "18px",
    fontWeight: "700",
    color: "white",
    background: "var(--btn-primary)",
    border: "none",
    borderRadius: "12px",
    cursor: "pointer",
    transition: "all 0.2s",
    boxShadow: "0 4px 12px color-mix(in srgb, var(--accent) 40%, transparent)",
  },
  startButtonDisabled: {
    background: "#cbd5e0",
    cursor: "not-allowed",
    boxShadow: "none",
    opacity: "0.7",
  },
  waitingForHost: {
    textAlign: "center",
    padding: "20px",
  },
  waitingForHostText: {
    fontSize: "16px",
    color: "var(--text-muted)",
    fontWeight: "500",
  },
  enterGameButton: {
    width: "100%",
    padding: "16px 24px",
    fontSize: "18px",
    fontWeight: "700",
    color: "white",
    background: "var(--btn-success)",
    border: "none",
    borderRadius: "12px",
    cursor: "pointer",
    transition: "all 0.2s",
    boxShadow: "0 4px 12px color-mix(in srgb, var(--btn-success) 40%, transparent)",
    marginBottom: "12px",
  },
  joinAsPlayerButton: {
    width: "100%",
    padding: "14px 24px",
    fontSize: "16px",
    fontWeight: "600",
    color: "white",
    background: "var(--btn-primary)",
    border: "none",
    borderRadius: "12px",
    cursor: "pointer",
    transition: "all 0.2s",
    boxShadow: "0 4px 12px color-mix(in srgb, var(--accent) 30%, transparent)",
  },
  error: {
    padding: "14px 18px",
    background: "color-mix(in srgb, var(--btn-danger) 15%, transparent)",
    color: "var(--btn-danger)",
    borderRadius: "12px",
    marginBottom: "20px",
    fontSize: "14px",
    fontWeight: "500",
    border: "1px solid color-mix(in srgb, var(--btn-danger) 40%, transparent)",
  },
};
